import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatDate, type GenerationDto, type PresentationDto } from '@pb/shared';
import { ApiError } from '../api/client';
import { listGroups } from '../api/groups';
import { regenerate } from '../api/generations';
import {
  copyPresentation,
  generatePresentation,
  getPresentation,
  listGenerations,
  retirePresentation,
  setPresentationStructure,
  unretirePresentation,
  updatePresentation,
} from '../api/presentations';
import { TypedConfirmDialog } from '../components/ConfirmDialogs';
import { Dialog } from '../components/Dialog';
import { DownloadGeneration, GenerationResult, Warnings, generationMeta } from '../components/Generations';
import { HistoryList } from '../components/History';
import { Tabs } from '../components/Nav';
import { ReorderList } from '../components/ReorderList';
import { DataView, EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { TagEditorDialog, TagList } from '../components/Tags';
import { useDetailCrumb, useProject, useProjectOwnerGuard } from '../lib/project';
import { move } from '../lib/reorder';
import { pageCountLabel, pluralize } from '../lib/text';
import { useResource } from '../lib/useResource';
import { NameDescriptionDialog } from './GroupsPage';
import { InlineTitle } from './SlidePage';

interface DraftGroup {
  key: string;
  group_id: number;
  name: string;
  page_count: number;
  hidden_page_count: number;
  retired: boolean;
}

interface DraftChapter {
  key: string;
  title: string;
  subtitle: string;
  numbered: boolean;
  groups: DraftGroup[];
}

let seq = 0;
const newKey = () => `k${++seq}`;

function toDraft(p: PresentationDto): DraftChapter[] {
  return [...p.chapters]
    .sort((a, b) => a.position - b.position)
    .map((c) => ({
      key: newKey(),
      title: c.title,
      subtitle: c.subtitle ?? '',
      numbered: c.numbered,
      groups: c.groups.map((g) => ({ key: newKey(), ...g })),
    }));
}

function serialize(chapters: DraftChapter[]): string {
  return JSON.stringify(chapters.map((c) => [c.title.trim(), c.subtitle.trim(), c.numbered, c.groups.map((g) => g.group_id)]));
}

function chapterNumbers(chapters: DraftChapter[]): (number | null)[] {
  let n = 0;
  return chapters.map((c) => (c.numbered ? ++n : null));
}

function GroupPickerDialog({
  chapterTitle,
  onClose,
  onAdd,
}: {
  chapterTitle: string;
  onClose: () => void;
  onAdd: (groups: DraftGroup[]) => void;
}) {
  const { project } = useProject();
  const groups = useResource(() => listGroups(project.id), [project.id]);
  const [text, setText] = useState('');
  const [chosen, setChosen] = useState<number[]>([]);
  const needle = text.trim().toLowerCase();
  const list = (groups.data ?? []).filter(
    (g) => !g.retired && (!needle || g.name.toLowerCase().includes(needle) || g.description.toLowerCase().includes(needle)),
  );

  return (
    <Dialog
      title={`Add groups to “${chapterTitle || 'Untitled chapter'}”`}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={chosen.length === 0}
            onClick={() =>
              onAdd(
                chosen
                  .map((id) => (groups.data ?? []).find((g) => g.id === id))
                  .filter((g): g is NonNullable<typeof g> => Boolean(g))
                  .map((g) => ({
                    key: newKey(),
                    group_id: g.id,
                    name: g.name,
                    page_count: g.page_count,
                    hidden_page_count: g.hidden_page_count,
                    retired: g.retired,
                  })),
              )
            }
          >
            Add {chosen.length ? pluralize(chosen.length, 'group') : 'groups'}
          </button>
        </>
      }
    >
      <input
        type="search"
        className="input"
        placeholder="Search groups by name"
        aria-label="Search groups"
        value={text}
        data-autofocus
        onChange={(event) => setText(event.target.value)}
      />
      {groups.error ? (
        <ErrorState error={groups.error} onRetry={() => void groups.reload()} />
      ) : groups.data === undefined ? (
        <Loading />
      ) : list.length === 0 ? (
        <p className="muted">
          {needle ? 'No groups match this search.' : 'There are no groups yet. Create groups in the Groups section first.'}
        </p>
      ) : (
        <div className="picker-list" role="group" aria-label="Groups">
          {list.map((g) => {
            const on = chosen.includes(g.id);
            return (
              <label key={g.id} className="picker-item checkbox">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => setChosen((c) => (on ? c.filter((x) => x !== g.id) : [...c, g.id]))}
                  aria-label={g.name}
                />
                <span className="grow">
                  <strong>{g.name}</strong>
                  {g.description ? <span className="small muted"> — {g.description}</span> : null}
                </span>
                <span className="small faint nowrap">
                  {pluralize(g.slide_count, 'slide')} · {pageCountLabel(g.page_count, g.hidden_page_count)}
                </span>
              </label>
            );
          })}
        </div>
      )}
      <p className="xsmall faint">The same group may appear in several chapters.</p>
    </Dialog>
  );
}

function Outline({
  presentation,
  chapters,
  dirty,
}: {
  presentation: PresentationDto;
  chapters: DraftChapter[];
  dirty: boolean;
}) {
  const numbers = chapterNumbers(chapters);
  const content = chapters.reduce((n, c) => n + c.groups.reduce((m, g) => m + g.page_count, 0), 0);
  const hiddenContent = chapters.reduce((n, c) => n + c.groups.reduce((m, g) => m + g.hidden_page_count, 0), 0);
  const agenda = presentation.show_agenda ? 1 : 0;
  const dividers = presentation.show_dividers ? chapters.length : 0;
  const total = 1 + agenda + dividers + content;
  const live = chapters
    .map((c, i) => (c.groups.length === 0 ? `Chapter “${c.title || `#${i + 1}`}” is empty.` : null))
    .filter((w): w is string => w !== null);
  const retired = chapters.flatMap((c) =>
    c.groups.filter((g) => g.retired).map((g) => `Group “${g.name}” in chapter “${c.title}” is retired.`),
  );
  const generationWarnings = presentation.latest_generation?.warnings ?? [];
  const server = dirty ? [] : presentation.warnings;
  const seen = new Set<string>();
  const current = [...live, ...retired, ...server].filter((w) => (seen.has(w) ? false : (seen.add(w), true)));
  const fromGeneration = generationWarnings.filter((w) => !seen.has(w));

  return (
    <section className="panel" aria-label="Outline">
      <div className="panel-title">
        <h3>Outline</h3>
        {dirty ? <span className="badge badge-warning">Unsaved</span> : null}
      </div>
      <ol className="outline">
        <li>
          <span>Title page</span>
          <span className="faint">1</span>
        </li>
        {agenda ? (
          <li>
            <span>Agenda</span>
            <span className="faint">1</span>
          </li>
        ) : null}
        {chapters.map((c, i) => {
          const pages = c.groups.reduce((m, g) => m + g.page_count, 0);
          const hiddenPages = c.groups.reduce((m, g) => m + g.hidden_page_count, 0);
          return (
            <li key={c.key}>
              <span>
                <strong>{numbers[i] !== null ? `${numbers[i]}  ` : ''}</strong>
                {c.title || <span className="faint">Untitled chapter</span>}
              </span>
              <span className="nowrap">
                {pageCountLabel(pages, hiddenPages)}
                {dividers ? <span className="faint"> +1</span> : null}
              </span>
            </li>
          );
        })}
        <li className="outline-total">
          <span>Total</span>
          <span>{pageCountLabel(total, hiddenContent)}</span>
        </li>
      </ol>
      <p className="xsmall faint" style={{ marginTop: 8 }}>
        Chapter counts are content pages; “+1” is the chapter divider. The total includes title, agenda and dividers.
      </p>
      <div className="stack" style={{ marginTop: 12 }}>
        <Warnings warnings={current} />
        <Warnings warnings={fromGeneration} title="From the last generation" />
      </div>
    </section>
  );
}

function StructureEditor({
  presentation,
  onReload,
  onSaved,
  onDraftChange,
}: {
  presentation: PresentationDto;
  onReload: () => Promise<void>;
  onSaved: (updated: PresentationDto) => void;
  onDraftChange: (chapters: DraftChapter[], dirty: boolean) => void;
}) {
  const [chapters, setChapters] = useState<DraftChapter[]>(() => toDraft(presentation));
  const [baseRevision, setBaseRevision] = useState(presentation.revision);
  const [baseSerialized, setBaseSerialized] = useState(() => serialize(toDraft(presentation)));
  const [picking, setPicking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState(false);
  const [saved, setSaved] = useState(false);
  const [armed, setArmed] = useState<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const serialized = serialize(chapters);
  const dirty = serialized !== baseSerialized;
  const numbers = chapterNumbers(chapters);
  const invalid = chapters.some((c) => !c.title.trim());

  useEffect(() => {
    onDraftChange(chapters, dirty);
  }, [serialized, dirty, chapters]);

  useEffect(() => {
    if (presentation.revision === baseRevision) return;
    if (!dirty) {
      const next = toDraft(presentation);
      setChapters(next);
      setBaseRevision(presentation.revision);
      setBaseSerialized(serialize(next));
    }
  }, [presentation]);

  const update = (key: string, patch: Partial<DraftChapter>) =>
    setChapters((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const moveChapter = (from: number, to: number) => {
    if (to < 0 || to >= chapters.length) return;
    setChapters((cs) => move(cs, from, to));
    window.requestAnimationFrame(() => {
      const selector = `[data-chapter-index="${to}"] [data-chapter-move="${to > from ? 'down' : 'up'}"]:not(:disabled)`;
      const fallback = `[data-chapter-index="${to}"] [data-chapter-move]:not(:disabled)`;
      (document.querySelector<HTMLButtonElement>(selector) ?? document.querySelector<HTMLButtonElement>(fallback))?.focus();
    });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setConflict(false);
    setSaved(false);
    try {
      const updated = await setPresentationStructure(
        presentation.id,
        baseRevision,
        chapters.map((c) => ({
          title: c.title.trim(),
          subtitle: c.subtitle.trim(),
          numbered: c.numbered,
          group_ids: c.groups.map((g) => g.group_id),
        })),
      );
      setBaseSerialized(serialized);
      setSaved(true);
      onSaved(updated);
    } catch (err) {
      if (err instanceof ApiError && err.reason === 'conflict') setConflict(true);
      else setError(err);
    } finally {
      setBusy(false);
    }
  };

  const pickingChapter = chapters.find((c) => c.key === picking);

  return (
    <div className="stack">
      {chapters.length === 0 ? (
        <EmptyState
          title="No chapters yet"
          action={
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setChapters([{ key: newKey(), title: 'Chapter 1', subtitle: '', numbered: true, groups: [] }])}
            >
              Add the first chapter
            </button>
          }
        >
          A presentation needs at least one chapter before it can be generated. Add a chapter, then add slide groups to
          it.
        </EmptyState>
      ) : (
        <ol className="list-plain" aria-label="Chapters" style={{ gap: 12 }}>
          {chapters.map((c, index) => {
            const classes = ['chapter-card'];
            if (dragIndex === index) classes.push('dragging');
            if (overIndex === index && dragIndex !== null && dragIndex !== index) classes.push('drop-target');
            const titleId = `chapter-title-${c.key}`;
            const subtitleId = `chapter-subtitle-${c.key}`;
            return (
              <li
                key={c.key}
                className={classes.join(' ')}
                data-chapter-index={index}
                draggable={armed === index}
                onDragStart={(event) => {
                  setDragIndex(index);
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', `chapter-${index}`);
                }}
                onDragOver={(event) => {
                  if (dragIndex === null) return;
                  event.preventDefault();
                  setOverIndex(index);
                }}
                onDrop={(event) => {
                  if (dragIndex === null) return;
                  event.preventDefault();
                  setChapters((cs) => move(cs, dragIndex, index));
                  setDragIndex(null);
                  setOverIndex(null);
                  setArmed(null);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                  setArmed(null);
                }}
              >
                <div className="chapter-head">
                  <span
                    className="drag-handle"
                    aria-hidden="true"
                    style={{ cursor: 'grab' }}
                    onMouseDown={() => setArmed(index)}
                    onMouseUp={() => setArmed(null)}
                    title="Drag to reorder"
                  >
                    ⋮⋮
                  </span>
                  <span className={`chapter-number${numbers[index] === null ? ' none' : ''}`}>
                    {numbers[index] ?? '–'}
                  </span>
                  <label htmlFor={titleId} className="visually-hidden">
                    Chapter {index + 1} title
                  </label>
                  <input
                    id={titleId}
                    className="input grow"
                    value={c.title}
                    maxLength={200}
                    placeholder="Chapter title"
                    onChange={(event) => update(c.key, { title: event.target.value })}
                  />
                  <label className="checkbox nowrap">
                    <input
                      type="checkbox"
                      checked={c.numbered}
                      onChange={(event) => update(c.key, { numbered: event.target.checked })}
                      aria-label={`Chapter ${index + 1} numbered`}
                    />
                    Numbered
                  </label>
                  <div className="reorder-buttons">
                    <button
                      type="button"
                      className="btn btn-icon"
                      data-chapter-move="up"
                      onClick={() => moveChapter(index, index - 1)}
                      disabled={index === 0}
                      aria-label={`Move chapter ${c.title || index + 1} up`}
                      title="Move chapter up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-icon"
                      data-chapter-move="down"
                      onClick={() => moveChapter(index, index + 1)}
                      disabled={index === chapters.length - 1}
                      aria-label={`Move chapter ${c.title || index + 1} down`}
                      title="Move chapter down"
                    >
                      ↓
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setChapters((cs) => cs.filter((x) => x.key !== c.key))}
                    aria-label={`Remove chapter ${c.title || index + 1}`}
                  >
                    Remove
                  </button>
                </div>
                <div className="chapter-head">
                  <span className="drag-handle" aria-hidden="true" style={{ visibility: 'hidden' }}>
                    ⋮⋮
                  </span>
                  <span
                    className={`chapter-number${numbers[index] === null ? ' none' : ''}`}
                    aria-hidden="true"
                    style={{ visibility: 'hidden' }}
                  >
                    {numbers[index] ?? '–'}
                  </span>
                  <label htmlFor={subtitleId} className="visually-hidden">
                    Chapter {index + 1} subtitle
                  </label>
                  <input
                    id={subtitleId}
                    className="input grow"
                    value={c.subtitle}
                    maxLength={300}
                    placeholder="Subtitle (optional)"
                    onChange={(event) => update(c.key, { subtitle: event.target.value })}
                  />
                </div>
                {c.groups.length === 0 ? (
                  <p className="small muted" style={{ paddingLeft: 44 }}>
                    This chapter is empty. Add groups to fill it.
                  </p>
                ) : (
                  <ReorderList
                    label={`Groups in chapter ${index + 1}`}
                    items={c.groups}
                    itemKey={(g) => g.key}
                    itemLabel={(g) => g.name}
                    onChange={(groups) => update(c.key, { groups })}
                    renderItem={(g) => (
                      <div className="row">
                        <GroupLink id={g.group_id} name={g.name} />
                        {g.retired ? <span className="badge badge-warning">Retired</span> : null}
                        <span className="spacer" />
                        <span className="small faint nowrap">{pageCountLabel(g.page_count, g.hidden_page_count)}</span>
                      </div>
                    )}
                    renderActions={(g) => (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => update(c.key, { groups: c.groups.filter((x) => x.key !== g.key) })}
                        aria-label={`Remove ${g.name} from the chapter`}
                      >
                        Remove
                      </button>
                    )}
                  />
                )}
                <div>
                  <button type="button" className="btn btn-sm" onClick={() => setPicking(c.key)}>
                    Add groups
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {chapters.length ? (
        <div>
          <button
            type="button"
            className="btn"
            onClick={() =>
              setChapters((cs) => [
                ...cs,
                { key: newKey(), title: `Chapter ${cs.length + 1}`, subtitle: '', numbered: true, groups: [] },
              ])
            }
          >
            Add chapter
          </button>
        </div>
      ) : null}
      {conflict ? (
        <div className="alert alert-warning row-between" role="alert">
          <span>
            Someone changed this presentation meanwhile. Reload to see the latest structure; your unsaved changes are
            dropped.
          </span>
          <button type="button" className="btn btn-sm" onClick={() => void onReload()}>
            Reload
          </button>
        </div>
      ) : null}
      {saved && !dirty ? (
        <div className="alert alert-success" role="status">
          Structure saved.
        </div>
      ) : null}
      {invalid ? <div className="alert alert-warning small">Every chapter needs a title before saving.</div> : null}
      <ErrorAlert error={error} />
      <div className="panel row" style={{ padding: 12 }}>
        <span className="small muted grow">{dirty ? 'You have unsaved changes to the structure.' : 'All changes saved.'}</span>
        <button
          type="button"
          className="btn"
          disabled={!dirty || busy}
          onClick={() => {
            const next = toDraft(presentation);
            setChapters(next);
            setBaseRevision(presentation.revision);
            setBaseSerialized(serialize(next));
          }}
        >
          Discard changes
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!dirty || busy || invalid}>
          {busy ? 'Saving…' : 'Save structure'}
        </button>
      </div>
      {pickingChapter ? (
        <GroupPickerDialog
          chapterTitle={pickingChapter.title}
          onClose={() => setPicking(null)}
          onAdd={(groups) => {
            update(pickingChapter.key, { groups: [...pickingChapter.groups, ...groups] });
            setPicking(null);
          }}
        />
      ) : null}
    </div>
  );
}

function GroupLink({ id, name }: { id: number; name: string }) {
  const { project } = useProject();
  return <Link to={`/projects/${project.id}/groups/${id}`}>{name}</Link>;
}

function GenerationsTab({ presentationId, onChanged }: { presentationId: number; onChanged: () => void }) {
  const generations = useResource(() => listGenerations(presentationId), [presentationId]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<GenerationDto | null>(null);

  const run = async (id: number) => {
    setBusyId(id);
    setError(null);
    setResult(null);
    try {
      setResult(await regenerate(id));
      await generations.refresh();
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  };

  if (generations.error) return <ErrorState error={generations.error} onRetry={() => void generations.reload()} />;
  if (generations.data === undefined) return <Loading label="Loading generations…" />;
  const list = [...generations.data].sort((a, b) => b.created_at.localeCompare(a.created_at));
  return (
    <div className="stack">
      {result ? <GenerationResult generation={result} /> : null}
      <ErrorAlert error={error} />
      {list.length === 0 ? (
        <EmptyState title="Not generated yet">Use “Generate” to build the first .pptx of this presentation.</EmptyState>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Generation</th>
                <th>Pages</th>
                <th>Template</th>
                <th>Warnings</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((g) => (
                <tr key={g.id}>
                  <td>
                    <div>{g.file_name}</div>
                    <div className="xsmall muted">{generationMeta(g)}</div>
                  </td>
                  <td>{g.page_count}</td>
                  <td>v{g.template_version_no}</td>
                  <td>
                    {g.warnings.length ? (
                      <span className="badge badge-warning" title={g.warnings.join('\n')}>
                        {pluralize(g.warnings.length, 'warning')}
                      </span>
                    ) : (
                      <span className="small faint">None</span>
                    )}
                  </td>
                  <td className="table-actions">
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <DownloadGeneration generation={g} />
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => void run(g.id)}
                        disabled={busyId !== null}
                        title="Rebuild the file from exactly this generation’s manifest"
                      >
                        {busyId === g.id ? 'Regenerating…' : 'Regenerate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="xsmall faint">
        Only the newest file of each kind is kept; older generations keep their manifest and can be regenerated.
      </p>
    </div>
  );
}

type PresentationTab = 'structure' | 'generations' | 'history';

export function PresentationPage() {
  const { presentationId } = useParams();
  const id = Number(presentationId);
  const { project } = useProject();
  const navigate = useNavigate();
  const presentation = useResource(() => getPresentation(id), [id]);
  const mismatched = useProjectOwnerGuard(presentation.data?.project_id);
  const [tab, setTab] = useState<PresentationTab>('structure');
  const [dialog, setDialog] = useState<'details' | 'tags' | 'retire' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [generated, setGenerated] = useState<GenerationDto | null>(null);
  const [draft, setDraft] = useState<{ chapters: DraftChapter[]; dirty: boolean } | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [generationsKey, setGenerationsKey] = useState(0);
  useDetailCrumb(presentation.data?.name);

  useEffect(() => {
    setGenerated(null);
    setDraft(null);
  }, [id]);

  useEffect(() => {
    if (draft?.dirty) setActionError(null);
  }, [draft?.dirty]);

  const act = async (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <DataView resource={presentation} what="presentation" loadingLabel="Loading presentation…">
      {(p) => {
        if (mismatched) return <Loading label="Loading presentation…" />;
        const chapters = draft?.chapters ?? toDraft(p);
        return (
          <div className="section">
            <div className="page-header">
              <div className="page-title">
                <InlineTitle
                  value={p.name}
                  label="presentation"
                  onSave={async (name) => {
                    await updatePresentation(p.id, { name });
                    await presentation.refresh();
                  }}
                />
                <div className="row-wrap">
                  {p.retired ? <span className="badge badge-warning">Retired</span> : null}
                  <span className="badge">{pluralize(p.chapter_count, 'chapter')}</span>
                  <span className="badge">{pageCountLabel(p.page_count, p.hidden_page_count)}</span>
                  <span className="small faint">Last change {formatDate(p.updated_at)}</span>
                </div>
                {p.subtitle ? <p style={{ fontSize: 'var(--text-lg)' }}>{p.subtitle}</p> : null}
                {p.description ? <p className="muted pre">{p.description}</p> : null}
                <div className="row-wrap">
                  <TagList tags={p.tags} />
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDialog('tags')}>
                    {p.tags.length ? 'Edit tags' : 'Add tags'}
                  </button>
                </div>
              </div>
              <div className="page-actions">
                <button type="button" className="btn" onClick={() => setDialog('details')}>
                  Edit details
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy !== null}
                  onClick={() =>
                    void act('copy', async () => {
                      const copy = await copyPresentation(p.id);
                      navigate(`/projects/${project.id}/presentations/${copy.id}`);
                    })
                  }
                >
                  {busy === 'copy' ? 'Copying…' : 'Copy'}
                </button>
                {p.retired ? (
                  <button
                    type="button"
                    className="btn"
                    disabled={busy !== null}
                    onClick={() =>
                      void act('unretire', async () => {
                        const updated = await unretirePresentation(p.id);
                        presentation.setData(updated);
                      })
                    }
                  >
                    Unretire
                  </button>
                ) : (
                  <button type="button" className="btn" onClick={() => setDialog('retire')}>
                    Retire
                  </button>
                )}
              </div>
            </div>
            <ErrorAlert error={actionError} />
            <div className="two-col">
              <div className="stack">
                <div className="panel row-wrap" style={{ padding: 12, gap: 24 }}>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={p.show_agenda}
                      disabled={busy !== null}
                      onChange={(event) =>
                        void act('switch', async () => {
                          await updatePresentation(p.id, { show_agenda: event.target.checked });
                          await presentation.refresh();
                        })
                      }
                      aria-label="Agenda slide"
                    />
                    Agenda slide
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={p.show_dividers}
                      disabled={busy !== null}
                      onChange={(event) =>
                        void act('switch', async () => {
                          await updatePresentation(p.id, { show_dividers: event.target.checked });
                          await presentation.refresh();
                        })
                      }
                      aria-label="Chapter divider slides"
                    />
                    Chapter divider slides
                  </label>
                </div>
                <Tabs
                  label="Presentation views"
                  active={tab}
                  onChange={setTab}
                  tabs={[
                    { key: 'structure', label: 'Chapters' },
                    { key: 'generations', label: 'Generations' },
                    { key: 'history', label: 'History' },
                  ]}
                />
                <div hidden={tab !== 'structure'}>
                  <StructureEditor
                    key={editorKey}
                    presentation={p}
                    onReload={async () => {
                      await presentation.refresh();
                      setDraft(null);
                      setEditorKey((k) => k + 1);
                    }}
                    onSaved={(updated) => presentation.setData(updated)}
                    onDraftChange={(cs, dirty) => setDraft({ chapters: cs, dirty })}
                  />
                </div>
                {tab === 'generations' ? (
                  <GenerationsTab
                    key={generationsKey}
                    presentationId={p.id}
                    onChanged={() => void presentation.refresh()}
                  />
                ) : null}
                {tab === 'history' ? <HistoryList target="presentation" id={p.id} /> : null}
              </div>
              <aside className="sidebar">
                <section className="panel stack" aria-label="Generate">
                  <h3>Generate</h3>
                  <p className="small muted">
                    Builds a .pptx on the latest template with the latest version of every group and slide.
                  </p>
                  {draft?.dirty ? (
                    <div className="alert alert-warning small">Save the structure first; generation uses the saved one.</div>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy !== null}
                    onClick={() =>
                      void act('generate', async () => {
                        setGenerated(null);
                        const g = await generatePresentation(p.id);
                        setGenerated(g);
                        setGenerationsKey((k) => k + 1);
                        await presentation.refresh();
                      })
                    }
                  >
                    {busy === 'generate' ? 'Generating…' : 'Generate'}
                  </button>
                  {generated ? (
                    <GenerationResult generation={generated} />
                  ) : p.latest_generation ? (
                    <div className="stack" style={{ gap: 6 }}>
                      <div className="small">
                        Last: {p.latest_generation.file_name}
                        <div className="xsmall muted">{generationMeta(p.latest_generation)}</div>
                      </div>
                      <DownloadGeneration generation={p.latest_generation} label="Download last generation" />
                    </div>
                  ) : null}
                </section>
                <Outline presentation={p} chapters={chapters} dirty={draft?.dirty ?? false} />
              </aside>
            </div>
            {dialog === 'details' ? (
              <NameDescriptionDialog
                title="Edit presentation details"
                submitLabel="Save"
                initialName={p.name}
                initialDescription={p.description}
                withSubtitle
                initialSubtitle={p.subtitle}
                onClose={() => setDialog(null)}
                onSubmit={async (name, description, subtitle) => {
                  await updatePresentation(p.id, { name, subtitle, description });
                  setDialog(null);
                  await presentation.refresh();
                }}
              />
            ) : null}
            {dialog === 'tags' ? (
              <TagEditorDialog
                target="presentation"
                ids={[p.id]}
                initial={p.tags.map((t) => t.id)}
                title="Tags of this presentation"
                onClose={() => setDialog(null)}
                onSaved={() => {
                  setDialog(null);
                  void presentation.refresh();
                }}
              />
            ) : null}
            {dialog === 'retire' ? (
              <TypedConfirmDialog
                title="Retire presentation"
                name={p.name}
                what="presentation name"
                actionLabel="Retire presentation"
                busyLabel="Retiring…"
                onClose={() => setDialog(null)}
                onConfirm={async (confirm) => {
                  const updated = await retirePresentation(p.id, confirm);
                  presentation.setData(updated);
                  setDialog(null);
                }}
              >
                <p>
                  A retired presentation is hidden unless “Show retired” is on and can be unretired at any time.
                  Retiring is refused while the presentation is part of a planned event.
                </p>
              </TypedConfirmDialog>
            ) : null}
          </div>
        );
      }}
    </DataView>
  );
}
